import React from 'react';
import { Heart } from 'lucide-react';
import { ThemeConfig, FavoriteItem } from '../types';
import { ImageService } from '../ImageService';

interface FavoritesProps {
  theme: ThemeConfig;
  favorites: FavoriteItem[];
  setFavorites: React.Dispatch<React.SetStateAction<FavoriteItem[]>>;
}

const Favorites: React.FC<FavoritesProps> = ({ theme, favorites, setFavorites }) => {
  const [loadedIds, setLoadedIds] = React.useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = React.useState<Set<number>>(new Set());

  const loadRandomImage = React.useCallback(async (id: number, category: string, forceRefresh = false) => {
    setIsLoading(prevIsLoading => {
      // Prevent duplicate requests
      if (prevIsLoading.has(id)) {
        return prevIsLoading;
      }

      // Immediately fetch the image
      (async () => {
        try {
          const imageUrl = await ImageService.fetchRandomImage(category, forceRefresh);
          if (imageUrl) {
            setFavorites(favs =>
              favs.map(fav => (fav.id === id ? { ...fav, imageUrl } : fav))
            );
            setLoadedIds(prev => new Set(prev).add(id));
          }
        } catch (error) {
          console.error('Error loading image:', error);
        } finally {
          setIsLoading(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
        }
      })();

      return new Set(prevIsLoading).add(id);
    });
  }, [setFavorites]);

  const toggleUnsplash = (id: number, currentUseUnsplash: boolean, category: string) => {
    const newUseUnsplash = !currentUseUnsplash;
    setFavorites(favs =>
      favs.map(fav => (fav.id === id ? { ...fav, useUnsplash: newUseUnsplash } : fav))
    );
    
    // If switching to Unsplash and we don't have a cached image, load one
    if (newUseUnsplash) {
      const favorite = favorites.find(f => f.id === id);
      if (favorite && !favorite.imageUrl) {
        loadRandomImage(id, category, false);
      }
    }
  };

  const getDisplayImage = (fav: FavoriteItem): string | null => {
    return fav.useUnsplash ? fav.imageUrl : fav.localImageUrl;
  };

  React.useEffect(() => {
    // Load images on initial mount if they don't have one and useUnsplash is true.
    // This will use the cache by default.
    favorites.forEach(fav => {
      if (fav.useUnsplash && !fav.imageUrl && !loadedIds.has(fav.id)) {
        loadRandomImage(fav.id, fav.category, false);
      }
    });
  }, [favorites, loadRandomImage, loadedIds]);

  return (
    <div>
      <h2 className={`text-2xl font-bold ${theme.text} mb-6`}>
        Things You Love ✨
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {favorites.map((item) => {
          const displayImage = getDisplayImage(item);
          return (
            <div
              key={item.id}
              className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-4 shadow-lg ${theme.shadow} border ${theme.border}`}
            >
              <div
                className={`aspect-square ${theme.secondary} rounded-xl mb-3 overflow-hidden flex items-center justify-center cursor-pointer hover:scale-105 transition-all`}
                onClick={() => item.useUnsplash && loadRandomImage(item.id, item.category, true)}
              >
                {displayImage ? (
                  <img src={displayImage} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <Heart className={`w-8 h-8 ${theme.textLight}`} />
                )}
              </div>
              <p className={`font-semibold ${theme.text} text-center mb-2`}>{item.name}</p>
              <div className="flex items-center justify-center gap-2">
                <input
                  type="checkbox"
                  id={`unsplash-${item.id}`}
                  checked={item.useUnsplash}
                  onChange={() => toggleUnsplash(item.id, item.useUnsplash, item.category)}
                  className="w-4 h-4 cursor-pointer"
                />
                <label htmlFor={`unsplash-${item.id}`} className={`text-xs ${theme.textLight} cursor-pointer`}>
                  Use Unsplash
                </label>
              </div>
              {item.useUnsplash && (
                <p className={`text-xs ${theme.textLight} text-center mt-1`}>Click image to refresh</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Favorites;